import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { AlertIconWrapper } from '@ui/AlertIconWrapper/AlertIconWrapper'
import { Body1, Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { RichIcon } from '@ui/components/RichIcon'
import { BellIcon } from '@ui/icons/Bell.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { PinIcon } from '@ui/icons/Pin.icon'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { showApptestRiskScreen } from '@utils/AccountAccess/accountProductStatuses'
import { getAgeOfDate } from '@utils/date.utils'
import { useWindowResize } from '@utils/domUtils'
import { NotificationText, useNotificationUnread } from '@utils/notifications'
import { CommonPaths, getProductAppropriatenessTestRiskPath } from '@utils/path'
import { isFullUrl } from '@utils/validations'

import { Notification, NotificationType } from '@model/Notification'
import { TickmillProductType } from '@model/TickmillProductType'

import { useFundingNav } from '@hooks/useFundingNav'

import { ActionModal } from '@shared/modals/Action.modal'
import { sqfWalkthrough } from '@shared/modals/Walkthrough/SQF'
import { WalkthroughModal } from '@shared/modals/Walkthrough/Walkthrough.modal'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import styles from './NotificationPreview.module.scss'

export interface NotificationPreviewProps {
  notification: Notification
  open?: () => void
  remove?: () => void
  read?: () => void
  hide?: () => void
  externalMode?: boolean
  className?: string
  cardId?: string
  showCloseModal?: boolean
}

const SideInfo = ({
  isPinned,
  externalMode,
  startDateTime,
}: {
  isPinned: boolean
  externalMode?: boolean
  startDateTime: string
}) => {
  return isPinned && !externalMode ? (
    <RichIcon
      bg='surface-primary-high-em'
      icon={{ icon: PinIcon, color: 'text-white' }}
      shape='circle'
      size='x24'
    />
  ) : (
    <div>
      <Body2 isParagraph className={styles.date}>
        {getAgeOfDate(startDateTime)}
      </Body2>
    </div>
  )
}

export const NotificationPreview: React.FC<NotificationPreviewProps> = (props) => {
  const {
    open,
    remove,
    externalMode,
    notification,
    read,
    hide,
    cardId,
    showCloseModal = false,
  } = props
  const { imageUrl, isPinned, ctaText, ctaUrl, isClosable, isRead } = notification
  const { startDateTime, title, notificationType, text } = notification
  const { refreshAllNotifications } = useNotificationUnread()
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const { account } = useAccountReadContext()
  const navigate = useNavigate()
  const { returnPath } = useFundingNav()
  const tracker = useDepositTracker()
  const walkthroughModal = useModal(WalkthroughModal, {
    steps: sqfWalkthrough,
    title: 'Spot-Quoted Futures',
    dark: true,
    productId: TickmillProductType.SQF,
  })

  const deleteNotificationModal = useModal(ActionModal, {
    title: t('Notifications.Delete Notification'),
    description: t('Notifications.This notification will be permanently deleted'),
  })

  const anchorTagToUrlMap: Record<string, string> = {
    '#SecuritySettings': '/profile/security-settings',
    '#Deposit': CommonPaths.Deposit,
    '#CFDSecuritySettings': '/profile/security-settings',
    '#CFDChangePassword': '/profile/security-settings',
    '#CFDTwoFactorAuthentication': '/profile/security-settings',
    '#CFDAddDepositForm': CommonPaths.TradersRoomWallets,
    '#CFDAddTAForm': CommonPaths.NewLiveTradingAccount,
    '#CFDAddCampaignTAForm': CommonPaths.NewLiveTradingAccount + '?campaign=true',
    '#IBDashboard': CommonPaths.IntroducingBrokerDashboard,
    '#IBWallets': CommonPaths.IntroducingBrokerWallets,
    '#IBReferralMaterials': '/dashboard/introducing-broker/referral-materials',
    '#CFDPlatforms': '/dashboard/traders-room/platforms/mt5',
    '#CFDVideoTutorials': '/dashboard/learning/trainings',
    '#CFDPersonalInfo': '/profile/personal-info',
    '#CFDDocumentManagement': CommonPaths.DocumentManagement,
    '#CFDBankAccounts': '/profile/bank-accounts',
    '#CFDPhoneNumbers': '/profile/phone-numbers',
    '#CFDAccountSettings': '/profile/account-settings',
    '#CFDCampaignResults': '/dashboard/traders-room/campaign-results',
    '#CFDWallets': CommonPaths.TradersRoomWallets,
    '#CFDTransferFundsForm': `${CommonPaths.TradersRoomWallets}/transfer`,
    '#CFDMarkets': '/dashboard/trade/markets',
    '#CFDPortfolio': '/dashboard/trade/portfolio/open',
    '#CFDAnalytics': '/dashboard/trade/analytics',
  }

  type NotificationCtaAction = 'SQFWalkthrough'

  const anchorTagToActionMap: Record<string, NotificationCtaAction> = {
    '#SQFWalkthrough': 'SQFWalkthrough',
  }

  const getUrlFromAnchorTag = (link: string | undefined): string | null => {
    if (!link || !link.startsWith('#')) {
      return null
    }
    return anchorTagToUrlMap[link] || null
  }

  const getActionFromAnchorTag = (link: string | undefined): NotificationCtaAction | null => {
    if (!link || !link.startsWith('#')) {
      return null
    }
    return anchorTagToActionMap[link] || null
  }

  const getInternalCtaPath = (link: string | undefined): string | null => {
    if (!link || link.startsWith('#')) {
      return null
    }
    try {
      const parsed = new URL(isFullUrl(link) ? link : `https://${link}`)
      if (parsed.host !== window.location.host) {
        return null
      }
      return `${parsed.pathname}${parsed.search}${parsed.hash}`
    } catch {
      return null
    }
  }

  const urlFromAnchorTag = getUrlFromAnchorTag(ctaUrl)
  const ctaAction = getActionFromAnchorTag(ctaUrl)
  const internalPath = ctaAction ? null : urlFromAnchorTag ?? getInternalCtaPath(ctaUrl)
  const isInternalLink = !!internalPath
  const url =
    internalPath ??
    (ctaAction || !ctaUrl ? undefined : `${!isFullUrl(ctaUrl) ? 'https://' : ''}${ctaUrl}`)

  const showCta = !!ctaText && !!(url || ctaAction)
  const allowDelete = isClosable !== false && ((!isPinned && isRead && !!remove) || !!hide)
  const overflown = text.length > 200 && !!open
  const showSideInfo = isMobile && !hide

  const openNotification = () => {
    if (!open) {
      return
    }
    if (overflown) {
      return open()
    }
    if (read) {
      read()
    }
    if (externalMode) {
      refreshAllNotifications()
    }
  }

  const handleDeleteClick = () => {
    if (showCloseModal) {
      deleteNotificationModal.open({ onConfirm: handleConfirmDeleteAction })
    } else {
      handleConfirmDeleteAction()
    }
  }

  const handleConfirmDeleteAction = () => {
    if (hide) {
      hide()
    } else if (remove) {
      remove()
    }
    deleteNotificationModal.close()
  }

  const NotificationIconPicker = () => {
    if (notificationType.id === NotificationType.Warning) {
      return (
        <AlertIconWrapper isActive={!isRead && !!open}>
          <WarningIcon size={22} color='text-warning-high-em' />
        </AlertIconWrapper>
      )
    }
    if (notificationType.id === NotificationType.Error) {
      return (
        <AlertIconWrapper isActive={!isRead && !!open}>
          <WarningIcon size={22} color='text-danger-high-em' />
        </AlertIconWrapper>
      )
    }
    if (notificationType.id === NotificationType.Platform) {
      return (
        <AlertIconWrapper isActive={!isRead && !!open}>
          <WarningIcon size={22} color='text-danger-high-em' />
        </AlertIconWrapper>
      )
    }
    return (
      <AlertIconWrapper isActive={!isRead && !!open}>
        <BellIcon size={22} />
      </AlertIconWrapper>
    )
  }

  const handleCtaClick = () => {
    if (url?.includes('/deposit')) {
      tracker.start({ entry_point: 'notification', notification_id: notification.id })
    }

    if (ctaAction === 'SQFWalkthrough') {
      if (showApptestRiskScreen(account, TickmillProductType.SQF)) {
        navigate(getProductAppropriatenessTestRiskPath(TickmillProductType.SQF))
      } else walkthroughModal.open()
    }
  }

  return (
    <div
      className={classNames(styles.notification, props.className, {
        [styles.external]: externalMode,
        [styles.warning]: notificationType.id === NotificationType.Warning,
        [styles.error]: notificationType.id === NotificationType.Error,
      })}
      id={cardId}
    >
      {!!imageUrl && (
        <img
          src={imageUrl}
          alt={title}
          className={classNames(styles.promoImage, {
            [styles.expanded]: !open,
          })}
        />
      )}

      <div className={styles.textBox}>
        <div className={styles.titleBox}>
          <p
            data-test='open-notification'
            onClick={openNotification}
            className={classNames(styles.title, {
              [styles.active]: open,
              [styles.spaceForClose]: !imageUrl,
            })}
          >
            {!imageUrl && <NotificationIconPicker />}
            {title}
          </p>
          <div className={styles.infoWrap}>
            {allowDelete && (
              <div className={styles.close}>
                <ActionIcon
                  size='x24'
                  bg='surface-white-smoke-1'
                  shape='circle'
                  border='outline-low-em'
                  icon={{ icon: CloseIcon, color: 'text-med-em' }}
                  onClick={handleDeleteClick}
                  data-test='remove-notification'
                />
              </div>
            )}
            {showSideInfo && (
              <div className={styles.datePin}>
                <SideInfo
                  isPinned={isPinned}
                  externalMode={externalMode}
                  startDateTime={startDateTime}
                />
              </div>
            )}
          </div>
        </div>

        <Body1 isParagraph className={styles.text}>
          <NotificationText
            overflown={overflown}
            text={text}
            open={open}
            className={styles.readMore}
          />
        </Body1>
        {showCta && (
          <div className={styles.buttonBox}>
            {ctaAction ? (
              <Button.Primary
                fullWidth={isMobile}
                size='lg'
                data-test='notification-cta-open'
                onClick={handleCtaClick}
              >
                {ctaText}
              </Button.Primary>
            ) : isInternalLink ? (
              <Link
                to={url ?? '/'}
                state={url?.includes('/deposit') ? { returnPath } : undefined}
                fullWidth={isMobile}
                onClick={handleCtaClick}
                underline={false}
              >
                <Button.Primary size='lg' fullWidth={isMobile} data-test='notification-cta-open'>
                  {ctaText}
                </Button.Primary>
              </Link>
            ) : (
              <Link href={url} fullWidth={isMobile} onClick={handleCtaClick} underline={false}>
                <Button.Primary size='lg' fullWidth={isMobile} data-test='notification-cta-open'>
                  {ctaText}
                </Button.Primary>
              </Link>
            )}
          </div>
        )}
      </div>
      {!externalMode && (
        <div className='is-hidden-mobile'>
          <SideInfo isPinned={isPinned} externalMode={externalMode} startDateTime={startDateTime} />
        </div>
      )}
    </div>
  )
}
